/** @format */

import {LockClosedIcon} from '@heroicons/react/solid'
import {useDisclosure} from '@mantine/hooks'
import * as Sentry from '@sentry/react'
import {PaymentMethod} from '@stripe/stripe-js'
import React, {PropsWithChildren} from 'react'
import {Link} from 'react-router-dom'

import {StripeElementsProvider} from '@src/components/tailwind/StripeElementsProvider'
import {UpgradeSubscriptionModal} from '@src/components/tailwind/UpgradeSubscriptionModal'

import {AnalyticsEvent, sendAnalytics} from '@src/analytics'
import {
  FeatureId,
  Plan,
  useAccountContext,
  useMinimumUpgradeProduct,
  useProductsApi,
} from '@src/contexts'
import {useChangePlan} from '@src/hooks/useChangePlan'

type UpgradeLinkProps = PropsWithChildren<{
  // extra properties to record on the "Upgrade Clicked" analytics event
  analyticsProperties?: Record<string, unknown>
  className?: string
  feature: FeatureId
  overrideFeatureName?: string
}>

// UpgradeLink dispatches to one of two independent paths. They diverge in
// both rendering and analytics, so each lives in its own component:
//   - UpgradePlanV2 (the experiment): opens a checkout modal in place
//   - UpgradePlan (the default): a plain link to the plans page
export function UpgradeLink(props: UpgradeLinkProps) {
  const {account} = useAccountContext()

  if (account.flags.upgrade_plan_v2) {
    return (
      <StripeElementsProvider>
        <UpgradePlanV2 {...props} />
      </StripeElementsProvider>
    )
  }

  return <UpgradePlan {...props} />
}

// Default path: names the cheapest qualifying plan, deep-links to it, and
// records recommended_product on the analytics event.
function UpgradePlan(props: UpgradeLinkProps) {
  const {
    analyticsProperties,
    children,
    className = 'primaryLink',
    feature,
  } = props

  const product = useMinimumUpgradeProduct(feature)

  function handleClick() {
    sendAnalytics(AnalyticsEvent.upgrade_clicked, {
      ...analyticsProperties,
      feature,
      recommended_product: product?.id,
    })
  }

  return (
    <Link
      className={className}
      onClick={handleClick}
      to={product ? `/plans?recommended=${product.id}` : '/plans'}
    >
      {children ?? <DefaultChildren productName={product?.name} />}
    </Link>
  )
}

// V2 path (experiment): opens the in-place checkout modal. Its analytics
// payload intentionally omits recommended_product — keep this shape stable
// while the experiment is running.
//
// A separate component is required to conditionally call useChangePlan.
function UpgradePlanV2(props: UpgradeLinkProps) {
  const {
    analyticsProperties,
    children,
    className = 'primaryLink',
    feature,
    overrideFeatureName,
  } = props

  const productsApi = useProductsApi()
  const f = productsApi.getFeatureById(feature)
  const products = productsApi.getProductsWithFeature(feature)
  const productNames = products.map(id => productsApi.getProductById(id).name)

  const changePlan = useChangePlan()
  const [isModalOpen, modal] = useDisclosure(false)

  function handleUpgradeClick() {
    modal.open()
    sendAnalytics(AnalyticsEvent.upgrade_clicked, {
      ...analyticsProperties,
      feature,
    })
  }

  async function handleConfirmUpgrade(
    plan: Plan,
    paymentMethod?: PaymentMethod,
  ): Promise<void> {
    await changePlan(plan, paymentMethod)
    close()
  }

  const featureName = overrideFeatureName ?? f.name
  const description = formatUpgradeModalDescription(featureName, productNames)
  return (
    <>
      <button className={className} onClick={handleUpgradeClick} type="button">
        {children ?? <DefaultChildren />}
      </button>

      <UpgradeSubscriptionModal
        description={description}
        isOpen={isModalOpen}
        onClose={() => modal.close()}
        onConfirmUpgrade={handleConfirmUpgrade}
        options={products}
        title={`Upgrade to unlock ${featureName}`}
      />
    </>
  )
}

function DefaultChildren(props: {productName?: string}) {
  const {productName} = props
  return (
    <>
      <LockClosedIcon className="inline-block h-4 w-4 align-text-bottom" />
      <span>
        &nbsp;{productName ? `Upgrade to ${productName}` : 'Upgrade to unlock'}
      </span>
    </>
  )
}

function formatUpgradeModalDescription(
  featureName: string,
  productNames: string[],
): string {
  const [p1, p2, p3] = productNames
  const prefix = `${featureName} requires a subscription`
  switch (productNames.length) {
    case 1:
      return `${prefix} to a ${p1} plan.`
    case 2:
      return `${prefix} to a ${p1} or ${p2} plan.`
    case 3:
      return `${prefix} to a ${p1}, ${p2} or ${p3} plan.`
    default:
      Sentry.captureException(
        new Error(
          `Encounter incorrect number of products (${productNames.length})`,
        ),
        {
          tags: {
            names: productNames.join(','),
          },
        },
      )
      return `${prefix}.`
  }
}
